import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Phone, Mail } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { copy, BUSINESS } from "../data/copy";

const legalLinks = [
    { to: "/about", label: { en: "About", te: "గురించి" } },
    { to: "/faq", label: { en: "FAQs", te: "ప్రశ్నలు" } },
    { to: "/storage-guide", label: { en: "Storage guide", te: "నిల్వ" } },
    { to: "/shipping", label: { en: "Shipping & returns", te: "డెలివరీ" } },
    { to: "/privacy", label: { en: "Privacy", te: "గోప్యత" } },
    { to: "/terms", label: { en: "Terms", te: "నిబంధనలు" } },
    { to: "/contact", label: { en: "Contact", te: "సంప్రదించండి" } },
];

export const Footer = () => {
    const { t, lang } = useLang();

    return (
        <footer
            className="relative bg-[color:var(--kp-ink)] text-[color:var(--kp-cream)] pt-20 md:pt-28 pb-8 overflow-hidden"
            data-testid="site-footer"
        >
            <div
                className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-25"
                style={{ background: "radial-gradient(circle, var(--kp-turmeric) 0%, transparent 65%)" }}
            />
            <div
                className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full opacity-15"
                style={{ background: "radial-gradient(circle, var(--kp-terracotta) 0%, transparent 60%)" }}
            />
            <div className="max-w-[1300px] mx-auto px-6 md:px-10 relative z-10">
                <div className="grid grid-cols-12 gap-6 mb-14 md:mb-20">
                    <div className="col-span-12 md:col-span-5">
                        <div className="flex items-center gap-2 mb-4">
                            <img
                                src="/img/logo-b.png"
                                alt="Bheema's Kumari Pickles"
                                className="w-12 h-12 rounded-full object-cover ring-1 ring-[color:var(--kp-turmeric)]/40"
                                data-testid="footer-logo-img"
                            />
                            <span className="font-display font-bold text-2xl">Bheema's Kumari</span>
                            <span className="font-display italic text-2xl text-[color:var(--kp-turmeric)]">Pickles</span>
                        </div>
                        <p className={`text-xl md:text-2xl leading-snug mb-6 ${
                            lang === "te" ? "font-telugu-display" : "font-display italic"
                        }`}>
                            {t(copy.footer.tagline)}
                        </p>
                        <div className="flex items-center gap-3 mb-4">
                            <a
                                href={BUSINESS.instagram}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram"
                                className="w-10 h-10 rounded-full bg-[color:var(--kp-cream)]/10 hover:bg-[color:var(--kp-turmeric)] hover:text-[color:var(--kp-ink)] flex items-center justify-center transition-colors"
                                data-testid="social-instagram"
                            >
                                <Instagram size={16} />
                            </a>
                            <a
                                href={BUSINESS.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Facebook"
                                className="w-10 h-10 rounded-full bg-[color:var(--kp-cream)]/10 hover:bg-[color:var(--kp-turmeric)] hover:text-[color:var(--kp-ink)] flex items-center justify-center transition-colors"
                                data-testid="social-facebook"
                            >
                                <Facebook size={16} />
                            </a>
                            <span className="text-[11px] tracking-mono-wide text-[color:var(--kp-cream)]/60 ml-1">
                                {BUSINESS.instagram_handle}
                            </span>
                        </div>
                        {(BUSINESS.gst || BUSINESS.fssai) && (
                            <div className="mt-5 space-y-1 text-[11px] tracking-mono-wide text-[color:var(--kp-cream)]/55">
                                {BUSINESS.gst && (
                                    <div data-testid="footer-gstin">
                                        {t(copy.footer.gst)}: {BUSINESS.gst}
                                    </div>
                                )}
                                {BUSINESS.fssai && (
                                    <div data-testid="footer-fssai">
                                        {t(copy.footer.fssai)}: {BUSINESS.fssai}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="col-span-6 md:col-span-3">
                        <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-turmeric)] mb-4">SHOP</div>
                        <ul className="space-y-2 text-sm">
                            <li><Link to="/shop" className="hover:text-[color:var(--kp-turmeric)]">All pickles</Link></li>
                            <li><Link to="/category/veg" className="hover:text-[color:var(--kp-turmeric)]">Veg</Link></li>
                            <li><Link to="/category/nonveg" className="hover:text-[color:var(--kp-turmeric)]">Non-Veg</Link></li>
                            <li><Link to="/category/sweet" className="hover:text-[color:var(--kp-turmeric)]">Sweet</Link></li>
                        </ul>
                    </div>

                    <div className="col-span-6 md:col-span-2">
                        <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-turmeric)] mb-4">KITCHEN</div>
                        <ul className="space-y-2 text-sm">
                            {legalLinks.map((l) => (
                                <li key={l.to}>
                                    <Link to={l.to} className="hover:text-[color:var(--kp-turmeric)] transition-colors">
                                        {t(l.label)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="col-span-12 md:col-span-2">
                        <div className="text-[10px] tracking-mono-wide text-[color:var(--kp-turmeric)] mb-4">REACH</div>
                        <ul className="space-y-2 text-sm">
                            <li className="flex items-center gap-2"><Phone size={12} /> {BUSINESS.phone_display}</li>
                            <li className="flex items-center gap-2"><Mail size={12} /> {BUSINESS.email}</li>
                            <li className="text-[color:var(--kp-cream)]/70 pt-2">{t(BUSINESS.address)}</li>
                            <li className="text-[color:var(--kp-cream)]/70">{t(BUSINESS.hours)}</li>
                        </ul>
                    </div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
                    className="giant-type text-[color:var(--kp-cream)] text-[20vw] md:text-[14vw] leading-[0.85] tracking-editorial"
                    aria-hidden="true"
                >
                    KUMARI
                    <span className="italic text-[color:var(--kp-turmeric)]"> pickles</span>
                </motion.div>

                <div className="mt-10 pt-6 border-t border-[color:var(--kp-cream)]/12 flex flex-col md:flex-row justify-between gap-3 text-[11px] tracking-mono-wide text-[color:var(--kp-cream)]/60">
                    <span className={lang === "te" ? "font-telugu-body" : ""} data-testid="footer-rights">
                        {t(copy.footer.rights)}
                    </span>
                    <span>Secure payments · Razorpay · UPI · Cards</span>
                </div>
            </div>
        </footer>
    );
};
